import { useMemo, useState } from "react";
import {
    AlertCircle,
    Clock,
    DollarSign,
    Image as ImageIcon,
    Layers,
    MousePointerClick,
    Play,
    Radio,
    Send,
    TrendingUp,
    Users,
} from "lucide-react";
import {
    Area,
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    ComposedChart,
    Line,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { useEsteiraGroup } from "../api/queries";
import type { SortBy } from "../api/reportsApi";
import { useDashboardStore } from "../store/dashboardStore";
import { PageHeader } from "../components/PageHeader";
import { PageShell } from "../components/PageShell";
import { SectionCard } from "../components/SectionCard";
import { KpiCard } from "../components/KpiCard";
import { LegendDot } from "../components/BadgeHi";
import { DashSkeleton, SkelText } from "../components/Skeleton";
import { ApiBanner } from "../components/ApiBanner";
import { Segmented } from "../components/Segmented";
import {
    cur,
    deltaDir,
    fmt,
    fmtDelta,
    fmtMoneyFull,
    fmtPct,
} from "../lib/format";
import { calcCtr } from "../lib/numbers";
import { cn } from "@/lib/utils";
import type { EsteiraTopBroadcast } from "../types/reports";

export function EsteiraReportView({ esId }: { esId?: string }) {
    const setView = useDashboardStore((s) => s.setView);
    const lastCrumbLabel = useDashboardStore(
        (s) => s.breadcrumbs[s.breadcrumbs.length - 1]?.label
    );
    const [sortBy, setSortBy] = useState<SortBy>("revenue");
    const { data, error, isLoading, refetch } = useEsteiraGroup(esId, sortBy);
    const loading = isLoading && !data;

    const e = data?.esteira;
    const k = data?.kpis;
    const revenueDaily = data?.revenueDaily ?? [];
    const topBcsRaw = data?.topBroadcasts ?? [];
    const revenueByHour = data?.revenueByHour ?? [];
    const revenueByHourMeta = data?.revenueByHourMeta;
    const heatmap = data?.heatmap ?? [];

    // Client-side reorder is harmless even though we re-fetch on sortBy change
    // — backend usually returns pre-sorted but be defensive.
    const topBcs = useMemo(() => {
        const ctrOf = (b: EsteiraTopBroadcast) =>
            b.ctr != null && b.ctr > 0
                ? b.ctr
                : calcCtr(b.clicks || 0, b.sends || 0);
        const keyFn =
            sortBy === "ctr"
                ? ctrOf
                : sortBy === "sends"
                  ? (b: EsteiraTopBroadcast) => b.sends || 0
                  : (b: EsteiraTopBroadcast) => b.revenue || 0;
        return [...topBcsRaw].sort((a, b) => keyFn(b) - keyFn(a));
    }, [topBcsRaw, sortBy]);

    // Fallback CTR — backend sometimes returns null even with clicks/sends.
    const ctrFallback =
        k?.ctr != null && cur(k.ctr) > 0
            ? cur(k.ctr)
            : calcCtr(k?.clicks || 0, cur(k?.sends));

    const dailySeries = revenueDaily.map((p) => ({
        d: (p.date || "").slice(-5),
        revenue: p.revenue || 0,
        sends: p.sends || 0,
        clicks: p.clicks || 0,
    }));

    const heroTitle = loading
        ? "Carregando…"
        : e?.name || lastCrumbLabel || "Esteira";
    const heroSubtitle = e
        ? `${e.countryCode || "—"} · ${
              e.status ? (e.status === "active" ? "Ativa" : "Pausada") : "—"
          } · desde ${e.createdAt ? (e.createdAt || "").slice(0, 10) : "—"}`
        : undefined;

    const hero = (
        <PageHeader
            icon={Radio}
            title={heroTitle}
            subtitle={heroSubtitle}
        />
    );

    if (error && !data) {
        return (
            <PageShell hero={hero}>
                <ApiBanner error={error} onRetry={() => refetch()} />
            </PageShell>
        );
    }

    return (
        <PageShell hero={hero}>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4">
                <KpiCard
                    icon={DollarSign}
                    iconColor="text-emerald-500"
                    label="Receita"
                    value={
                        loading ? (
                            <SkelText />
                        ) : (
                            "$" +
                            Number(cur(k?.revenue)).toLocaleString("pt-BR", {
                                maximumFractionDigits: 2,
                            })
                        )
                    }
                    delta={fmtDelta(k?.revenue)}
                    deltaDir={deltaDir(k?.revenue)}
                />
                <KpiCard
                    icon={Send}
                    iconColor="text-blue-500"
                    label="Broadcasts"
                    value={loading ? <SkelText /> : fmt(k?.broadcasts ?? 0)}
                />
                <KpiCard
                    icon={Users}
                    iconColor="text-violet-500"
                    label="Envios"
                    value={
                        loading ? (
                            <SkelText />
                        ) : (
                            fmt(Math.round(cur(k?.sends)))
                        )
                    }
                    delta={fmtDelta(k?.sends)}
                    deltaDir={deltaDir(k?.sends)}
                />
                <KpiCard
                    icon={MousePointerClick}
                    iconColor="text-amber-500"
                    label="CTR"
                    value={
                        loading ? <SkelText /> : fmtPct(ctrFallback)
                    }
                    sub={
                        k?.clicks != null ? `${fmt(k.clicks)} cliques` : undefined
                    }
                    delta={fmtDelta(k?.ctr)}
                    deltaDir={deltaDir(k?.ctr)}
                />
                <KpiCard
                    icon={MousePointerClick}
                    iconColor="text-violet-500"
                    label="Cliques"
                    value={loading ? <SkelText /> : fmt(k?.clicks ?? 0)}
                />
                <KpiCard
                    icon={TrendingUp}
                    iconColor="text-emerald-500"
                    label="RPM"
                    value={
                        loading ? (
                            <SkelText />
                        ) : (
                            "$" + Number(k?.rpm ?? 0).toFixed(2)
                        )
                    }
                />
                <KpiCard
                    icon={Layers}
                    iconColor="text-rose-500"
                    label="Páginas"
                    value={loading ? <SkelText /> : fmt(k?.pagesCount ?? 0)}
                    sub="alvo do disparo"
                />
            </div>

            {/* Daily revenue chart */}
            <SectionCard
                icon={TrendingUp}
                title="Performance diária"
                subtitle="receita, envios e cliques por dia"
                right={
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                        <LegendDot color="#8b5cf6" label="Receita ($)" />
                        <LegendDot color="#3b82f6" label="Envios" />
                        <LegendDot color="#f59e0b" label="Cliques" />
                    </div>
                }
            >
                {loading ? (
                    <DashSkeleton h={280} />
                ) : dailySeries.length === 0 ? (
                    <EmptyMsg />
                ) : (
                    <div className="h-72">
                        <ResponsiveContainer>
                            <ComposedChart
                                data={dailySeries}
                                margin={{
                                    top: 5,
                                    right: 10,
                                    bottom: 0,
                                    left: -10,
                                }}
                            >
                                <defs>
                                    <linearGradient
                                        id="gEsteiraRpt"
                                        x1="0"
                                        y1="0"
                                        x2="0"
                                        y2="1"
                                    >
                                        <stop
                                            offset="0%"
                                            stopColor="#10b981"
                                            stopOpacity={0.35}
                                        />
                                        <stop
                                            offset="100%"
                                            stopColor="#10b981"
                                            stopOpacity={0}
                                        />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid
                                    strokeDasharray="3 3"
                                    stroke="#f1f5f9"
                                    vertical={false}
                                />
                                <XAxis
                                    dataKey="d"
                                    tick={{ fontSize: 11, fill: "#64748b" }}
                                    tickLine={false}
                                    axisLine={false}
                                />
                                <YAxis
                                    yAxisId="rev"
                                    tick={{ fontSize: 11, fill: "#10b981" }}
                                    tickLine={false}
                                    axisLine={false}
                                    tickFormatter={(v: number) =>
                                        "$" +
                                        (v >= 1000
                                            ? (v / 1000).toFixed(0) + "k"
                                            : v)
                                    }
                                />
                                <YAxis
                                    yAxisId="cnt"
                                    orientation="right"
                                    tick={{ fontSize: 11, fill: "#64748b" }}
                                    tickLine={false}
                                    axisLine={false}
                                    tickFormatter={(v: number) =>
                                        v >= 1000
                                            ? (v / 1000).toFixed(0) + "k"
                                            : String(v)
                                    }
                                />
                                <Tooltip
                                    contentStyle={{
                                        borderRadius: 8,
                                        border: "1px solid #e5e7eb",
                                        fontSize: 12,
                                    }}
                                    formatter={(value, name) => {
                                        if (name === "revenue")
                                            return [
                                                "$" +
                                                    Number(
                                                        value
                                                    ).toLocaleString("pt-BR", {
                                                        maximumFractionDigits: 2,
                                                    }),
                                                "Receita",
                                            ];
                                        if (name === "sends")
                                            return [fmt(Number(value)), "Envios"];
                                        if (name === "clicks")
                                            return [
                                                fmt(Number(value)),
                                                "Cliques",
                                            ];
                                        return [String(value), String(name)];
                                    }}
                                />
                                <Area
                                    yAxisId="rev"
                                    type="monotone"
                                    dataKey="revenue"
                                    stroke="#10b981"
                                    strokeWidth={2.2}
                                    fill="url(#gEsteiraRpt)"
                                />
                                <Line
                                    yAxisId="cnt"
                                    type="monotone"
                                    dataKey="sends"
                                    stroke="#64748b"
                                    strokeWidth={2}
                                    dot={false}
                                />
                                <Line
                                    yAxisId="cnt"
                                    type="monotone"
                                    dataKey="clicks"
                                    stroke="#f59e0b"
                                    strokeWidth={2}
                                    dot={false}
                                    strokeDasharray="4 4"
                                />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </SectionCard>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <SectionCard
                    icon={Send}
                    title="Top broadcasts"
                    subtitle="preview · clique pra abrir"
                    right={
                        <div className="flex items-center gap-2">
                            <span className="text-[11px] text-gray-400 hidden sm:inline">
                                ordenar:
                            </span>
                            <Segmented<SortBy>
                                value={sortBy}
                                onChange={setSortBy}
                                options={[
                                    { v: "revenue", l: "Receita" },
                                    { v: "ctr", l: "CTR" },
                                    { v: "sends", l: "Envios" },
                                ]}
                            />
                        </div>
                    }
                >
                    {loading ? (
                        <DashSkeleton h={300} />
                    ) : topBcs.length === 0 ? (
                        <EmptyMsg />
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {topBcs.slice(0, 6).map((b) => (
                                <BroadcastPreviewCard
                                    key={b.id}
                                    b={b}
                                    onClick={() =>
                                        setView(
                                            {
                                                name: "broadcast",
                                                contentHash:
                                                    b.contentHash || b.id,
                                                groupedIds: b.groupedIds,
                                            },
                                            b.name,
                                            { keepParent: true }
                                        )
                                    }
                                />
                            ))}
                        </div>
                    )}
                </SectionCard>

                <SectionCard
                    icon={Clock}
                    title="Receita por horário"
                    subtitle="rastreamento UTM da hora do disparo"
                >
                    {loading ? (
                        <DashSkeleton h={240} />
                    ) : revenueByHour.length === 0 ? (
                        <EmptyMsg />
                    ) : (
                        <HourRevenueChart
                            data={revenueByHour}
                            meta={revenueByHourMeta}
                        />
                    )}
                </SectionCard>
            </div>

            {heatmap.length > 0 && (
                <SectionCard
                    icon={Clock}
                    title="Heatmap dessa esteira"
                    subtitle="hora × dia · clique numa célula pra ver o detalhe"
                >
                    <EsteiraHeatmap cells={heatmap} />
                </SectionCard>
            )}
        </PageShell>
    );
}

// ── Hour revenue bar chart ────────────────────────────────────────
function HourRevenueChart({
    data,
    meta,
}: {
    data: Array<{ hour: number; revenue: number; sends: number; clicks: number }>;
    meta?: { attributedTotal: number; unattributedTotal: number; groupTotal: number };
}) {
    const buckets = useMemo(() => {
        return Array.from({ length: 24 }, (_, h) => {
            const found = data.find((d) => d.hour === h);
            return {
                h: String(h).padStart(2, "0"),
                hRaw: h,
                revenue: found?.revenue || 0,
                sends: found?.sends || 0,
                clicks: found?.clicks || 0,
            };
        });
    }, [data]);

    const max = Math.max(...buckets.map((b) => b.revenue), 0);
    const peakHour = max > 0 ? buckets.findIndex((b) => b.revenue === max) : -1;
    const total = buckets.reduce((s, b) => s + b.revenue, 0);
    const activeHours = buckets.filter((b) => b.revenue > 0).length;

    const unattributedPct =
        meta && meta.groupTotal > 0
            ? (meta.unattributedTotal / meta.groupTotal) * 100
            : 0;

    return (
        <div>
            {unattributedPct > 0 && (
                <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-2 mb-2 flex items-start gap-2">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span>
                        <strong>{unattributedPct.toFixed(0)}%</strong> da
                        receita não atribuída por hora (UTM truncado) — $
                        {meta!.unattributedTotal.toLocaleString("pt-BR", {
                            maximumFractionDigits: 2,
                        })}{" "}
                        de ${meta!.groupTotal.toLocaleString("pt-BR", {
                            maximumFractionDigits: 2,
                        })}
                        .
                    </span>
                </p>
            )}
            <div className="h-60">
                <ResponsiveContainer>
                    <BarChart
                        data={buckets}
                        margin={{ top: 5, right: 5, bottom: 0, left: -10 }}
                    >
                        <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="#f1f5f9"
                            vertical={false}
                        />
                        <XAxis
                            dataKey="h"
                            tick={{ fontSize: 10, fill: "#64748b" }}
                            tickLine={false}
                            axisLine={false}
                            interval={1}
                        />
                        <YAxis
                            tick={{ fontSize: 11, fill: "#64748b" }}
                            tickLine={false}
                            axisLine={false}
                            tickFormatter={(v: number) =>
                                "$" +
                                (v >= 1000
                                    ? (v / 1000).toFixed(0) + "k"
                                    : v)
                            }
                        />
                        <Tooltip
                            contentStyle={{
                                borderRadius: 8,
                                border: "1px solid #e5e7eb",
                                fontSize: 12,
                            }}
                            formatter={(v, name) => {
                                if (name === "revenue")
                                    return [
                                        "$" +
                                            Number(v).toLocaleString("pt-BR", {
                                                maximumFractionDigits: 2,
                                            }),
                                        "Receita",
                                    ];
                                return [String(v), String(name)];
                            }}
                            labelFormatter={(h) => `Hora ${h}:00 → ${h}:59`}
                        />
                        <Bar dataKey="revenue" radius={[4, 4, 0, 0]}>
                            {buckets.map((b, i) => (
                                <Cell
                                    key={i}
                                    fill={
                                        i === peakHour
                                            ? "#f59e0b"
                                            : b.revenue > 0
                                              ? "#8b5cf6"
                                              : "#e2e8f0"
                                    }
                                />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                <div className="bg-amber-50 border border-amber-100 rounded-lg p-2">
                    <p className="text-[10px] uppercase font-semibold text-amber-700 tracking-wider">
                        Hora pico
                    </p>
                    <p className="text-sm font-bold text-gray-900">
                        {peakHour >= 0
                            ? `${String(peakHour).padStart(2, "0")}h`
                            : "—"}
                        <span className="text-[10px] font-mono text-amber-700 ml-1.5 tabular-nums">
                            ${max.toFixed(0)}
                        </span>
                    </p>
                </div>
                <div className="bg-violet-50 border border-violet-100 rounded-lg p-2">
                    <p className="text-[10px] uppercase font-semibold text-violet-700 tracking-wider">
                        Total
                    </p>
                    <p className="text-sm font-bold text-gray-900 font-mono tabular-nums">
                        $
                        {total.toLocaleString("pt-BR", {
                            maximumFractionDigits: 0,
                        })}
                    </p>
                </div>
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-2">
                    <p className="text-[10px] uppercase font-semibold text-blue-700 tracking-wider">
                        Média / h ativa
                    </p>
                    <p className="text-sm font-bold text-gray-900 font-mono tabular-nums">
                        $
                        {(total / Math.max(activeHours, 1)).toFixed(0)}
                    </p>
                </div>
            </div>
        </div>
    );
}

// ── Broadcast preview card (local) ────────────────────────────────
function BroadcastPreviewCard({
    b,
    onClick,
}: {
    b: EsteiraTopBroadcast;
    onClick: () => void;
}) {
    const ctrVal =
        b.ctr != null && b.ctr > 0
            ? b.ctr
            : b.sends > 0 && b.clicks > 0
              ? (b.clicks / b.sends) * 100
              : null;
    const isGrouped = b.isGrouped || (b.dispatches ?? 0) > 1;
    const dispatchCount =
        b.dispatches || (b.groupedIds ? b.groupedIds.length : 1);
    const hasMedia = b.previewMediaUrl || b.media === "image" || b.media === "video";

    return (
        <button
            type="button"
            onClick={onClick}
            className="text-left bg-white border border-gray-100 rounded-xl overflow-hidden hover:shadow-md hover:border-violet-200 hover:-translate-y-px transition-all flex flex-col"
        >
            <div className="bg-gradient-to-br from-slate-50 to-slate-100 px-3 pt-3 pb-2 border-b border-gray-100 relative">
                {isGrouped && (
                    <div className="absolute top-2 right-2 z-10">
                        <span className="inline-flex items-center gap-1 bg-amber-500/95 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md shadow-sm">
                            <Layers className="w-3 h-3" />
                            {dispatchCount} disparos
                        </span>
                    </div>
                )}

                {hasMedia ? (
                    b.previewMediaUrl ? (
                        <div className="bg-slate-200 rounded-lg h-24 overflow-hidden mb-2">
                            <img
                                src={b.previewMediaUrl}
                                alt=""
                                className="object-cover w-full h-full"
                                onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                }}
                            />
                        </div>
                    ) : b.media === "video" ? (
                        <div className="bg-gradient-to-br from-slate-700 to-slate-900 rounded-lg h-24 mb-2 flex items-center justify-center text-white">
                            <Play className="w-6 h-6" />
                        </div>
                    ) : (
                        <div className="bg-gradient-to-br from-slate-300 to-slate-400 rounded-lg h-24 mb-2 flex items-center justify-center text-white">
                            <ImageIcon className="w-6 h-6 opacity-60" />
                        </div>
                    )
                ) : null}

                <div className="bg-white rounded-lg shadow-sm border border-gray-100 px-2.5 py-1.5 min-h-[40px]">
                    {b.previewTitle && (
                        <p className="text-[11px] font-bold text-gray-900 leading-tight line-clamp-1">
                            {b.previewTitle}
                        </p>
                    )}
                    {b.previewSubtitle && (
                        <p className="text-[10px] text-gray-600 line-clamp-1 mt-0.5">
                            {b.previewSubtitle}
                        </p>
                    )}
                    {!b.previewTitle && !b.previewSubtitle && (
                        <p className="text-[11px] text-gray-700 line-clamp-2">
                            {b.name || "—"}
                        </p>
                    )}
                </div>
            </div>

            <div className="p-3 flex-1 flex flex-col gap-2">
                <div className="flex items-start justify-between gap-2">
                    <h4 className="font-semibold text-gray-800 text-xs truncate flex-1">
                        {b.name}
                    </h4>
                    <span className="text-[10px] text-gray-400 font-mono shrink-0">
                        {isGrouped ? `${dispatchCount}×` : `#${b.id}`}
                    </span>
                </div>
                <div
                    className={cn(
                        "grid gap-2 pt-2 border-t border-gray-100",
                        ctrVal != null ? "grid-cols-3" : "grid-cols-2"
                    )}
                >
                    <div>
                        <p className="text-[9px] text-gray-400 uppercase font-medium tracking-wider">
                            Enviadas
                        </p>
                        <p className="text-xs font-semibold text-gray-800 font-mono tabular-nums">
                            {fmt(b.sends ?? 0)}
                        </p>
                    </div>
                    {ctrVal != null && (
                        <div>
                            <p className="text-[9px] text-gray-400 uppercase font-medium tracking-wider">
                                CTR
                            </p>
                            <span
                                className={cn(
                                    "inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded-full font-mono tabular-nums",
                                    ctrVal >= 8
                                        ? "bg-emerald-50 text-emerald-700"
                                        : ctrVal >= 3
                                          ? "bg-blue-50 text-blue-700"
                                          : "bg-gray-100 text-gray-600"
                                )}
                            >
                                {ctrVal.toFixed(2)}%
                            </span>
                        </div>
                    )}
                    <div>
                        <p className="text-[9px] text-gray-400 uppercase font-medium tracking-wider">
                            Receita
                        </p>
                        <p className="text-xs font-bold text-emerald-600 font-mono tabular-nums">
                            $
                            {Number(b.revenue || 0).toLocaleString("pt-BR", {
                                maximumFractionDigits: 0,
                            })}
                        </p>
                    </div>
                </div>
            </div>
        </button>
    );
}

// ── Heatmap (local copy — kept in sync with EsteiraView) ──────────
type HeatCell = { weekday: number; hour: number; clicks: number; revenue: number };
type Focus = { r: number; c: number } | null;

const ROW_TO_WEEKDAY = [1, 2, 3, 4, 5, 6, 0]; // row 0 = seg (weekday 1), row 6 = dom (weekday 0)
const DAY_SHORT = ["seg", "ter", "qua", "qui", "sex", "sáb", "dom"];
const DAY_LONG = [
    "Segunda",
    "Terça",
    "Quarta",
    "Quinta",
    "Sexta",
    "Sábado",
    "Domingo",
];

function EsteiraHeatmap({ cells }: { cells: HeatCell[] }) {
    const [hover, setHover] = useState<Focus>(null);
    const [selected, setSelected] = useState<Focus>(null);

    const cellMap = useMemo(() => {
        const m = new Map<string, HeatCell>();
        cells.forEach((c) => m.set(`${c.weekday}-${c.hour}`, c));
        return m;
    }, [cells]);

    const max = useMemo(
        () => Math.max(...cells.map((c) => c.revenue || 0), 0),
        [cells]
    );

    if (max <= 0) {
        return (
            <div className="bg-slate-50 rounded-lg py-8 text-center text-gray-400 border border-gray-100">
                <Clock className="w-6 h-6 mx-auto mb-2" />
                <p className="text-sm">Sem atividade no período</p>
            </div>
        );
    }

    const activeWeekdays = new Set(
        cells.filter((c) => (c.revenue || 0) > 0).map((c) => c.weekday)
    ).size;
    const shortRangeHint = activeWeekdays <= 1;

    const focus = selected || hover;
    const focusWeekday = focus ? ROW_TO_WEEKDAY[focus.r] : null;
    const focusCell =
        focus && focusWeekday != null
            ? cellMap.get(`${focusWeekday}-${focus.c}`)
            : null;

    const rowTotal =
        focus && focusWeekday != null
            ? cells
                  .filter((c) => c.weekday === focusWeekday)
                  .reduce((s, c) => s + (c.revenue || 0), 0)
            : 0;
    const colTotal = focus
        ? cells
              .filter((c) => c.hour === focus.c)
              .reduce((s, c) => s + (c.revenue || 0), 0)
        : 0;
    const grandTotal = cells.reduce((s, c) => s + (c.revenue || 0), 0);

    return (
        <div>
            {shortRangeHint && (
                <div className="mb-3 bg-blue-50 border border-blue-100 rounded-lg p-3 text-xs text-blue-900 flex items-start gap-2">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span>
                        O período filtrado cai em{" "}
                        <strong>um único dia da semana</strong>, então só essa
                        linha tem dados. O mapa fica mais útil com{" "}
                        <strong>30 ou 90 dias</strong> (revela o padrão
                        semanal).
                    </span>
                </div>
            )}
            <div
                className="grid gap-1"
                style={{ gridTemplateColumns: "44px repeat(24, 1fr)" }}
            >
                <div />
                {Array.from({ length: 24 }, (_, i) => (
                    <div
                        key={i}
                        className={cn(
                            "text-[10px] text-center font-mono transition-colors",
                            focus?.c === i
                                ? "text-violet-700 font-bold"
                                : "text-gray-400"
                        )}
                    >
                        {i % 2 === 0 ? String(i).padStart(2, "0") : ""}
                    </div>
                ))}
                {Array.from({ length: 7 }, (_, r) => {
                    const weekday = ROW_TO_WEEKDAY[r];
                    return (
                        <div key={`row-${r}`} className="contents">
                            <div
                                className={cn(
                                    "text-xs font-medium flex items-center transition-colors",
                                    focus?.r === r
                                        ? "text-violet-700 font-bold"
                                        : "text-gray-500"
                                )}
                            >
                                {DAY_SHORT[r]}
                            </div>
                            {Array.from({ length: 24 }, (_, c) => {
                                const cell = cellMap.get(`${weekday}-${c}`);
                                const v = cell?.revenue || 0;
                                const clicks = cell?.clicks || 0;
                                const a = v / max;
                                const isHot = a > 0.7;
                                const bg =
                                    v === 0
                                        ? "rgba(241,245,249,0.7)"
                                        : isHot
                                          ? `rgba(245, 158, 11, ${a})`
                                          : `rgba(139, 92, 246, ${Math.max(a * 0.7, 0.18)})`;
                                const isSelected =
                                    selected?.r === r && selected?.c === c;
                                const isHovered =
                                    hover?.r === r && hover?.c === c;
                                const isFocused = isSelected || isHovered;
                                return (
                                    <button
                                        key={`cell-${r}-${c}`}
                                        type="button"
                                        onMouseEnter={() =>
                                            setHover({ r, c })
                                        }
                                        onMouseLeave={() => setHover(null)}
                                        onClick={() =>
                                            setSelected(
                                                isSelected ? null : { r, c }
                                            )
                                        }
                                        className="rounded-sm transition-all relative"
                                        style={{
                                            height: 30,
                                            background: bg,
                                            border: isFocused
                                                ? "1.5px solid #6d28d9"
                                                : "1px solid rgba(255,255,255,0.6)",
                                            transform: isFocused
                                                ? "scale(1.18)"
                                                : "scale(1)",
                                            zIndex: isFocused ? 5 : 1,
                                            boxShadow: isSelected
                                                ? "0 4px 12px rgba(139,92,246,0.4)"
                                                : "none",
                                        }}
                                        title={`${DAY_SHORT[r]} ${c}h · ${fmtMoneyFull(v)}${clicks ? ` · ${fmt(clicks)} cliques` : ""}`}
                                    />
                                );
                            })}
                        </div>
                    );
                })}
            </div>

            {focus && (
                <div className="mt-4 bg-gradient-to-br from-violet-50 to-indigo-50 border border-violet-200 rounded-xl p-4 grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <p className="text-[10px] uppercase font-semibold tracking-wider text-violet-700 mb-1">
                            {selected
                                ? "Célula selecionada"
                                : "Passando o mouse"}
                        </p>
                        <p className="text-lg font-bold text-gray-900 leading-tight">
                            {DAY_LONG[focus.r]}
                        </p>
                        <p className="text-sm font-mono text-gray-700 tabular-nums">
                            {String(focus.c).padStart(2, "0")}:00 –{" "}
                            {String(focus.c).padStart(2, "0")}:59
                        </p>
                    </div>
                    <div>
                        <p className="text-[10px] uppercase font-semibold tracking-wider text-gray-500 mb-1">
                            Receita
                        </p>
                        <p className="text-2xl font-bold text-emerald-600 font-mono tabular-nums">
                            $
                            {(focusCell?.revenue || 0).toLocaleString("pt-BR", {
                                maximumFractionDigits: 2,
                            })}
                        </p>
                        {focusCell?.clicks != null && (
                            <p className="text-[11px] text-gray-500 mt-0.5 font-mono tabular-nums">
                                {fmt(focusCell.clicks)} cliques
                            </p>
                        )}
                    </div>
                    <div>
                        <p className="text-[10px] uppercase font-semibold tracking-wider text-gray-500 mb-1">
                            % da hora ({focus.c}h)
                        </p>
                        <p className="text-xl font-bold text-gray-900 font-mono tabular-nums">
                            {colTotal > 0
                                ? (
                                      ((focusCell?.revenue || 0) / colTotal) *
                                      100
                                  ).toFixed(1)
                                : "0"}
                            %
                        </p>
                        <p className="text-[11px] text-gray-500 mt-0.5 font-mono tabular-nums">
                            total {focus.c}h: $
                            {colTotal.toFixed(2)}
                        </p>
                    </div>
                    <div>
                        <p className="text-[10px] uppercase font-semibold tracking-wider text-gray-500 mb-1">
                            % do dia ({DAY_SHORT[focus.r]})
                        </p>
                        <p className="text-xl font-bold text-gray-900 font-mono tabular-nums">
                            {rowTotal > 0
                                ? (
                                      ((focusCell?.revenue || 0) / rowTotal) *
                                      100
                                  ).toFixed(1)
                                : "0"}
                            %
                        </p>
                        <p className="text-[11px] text-gray-500 mt-0.5 font-mono tabular-nums">
                            total {DAY_SHORT[focus.r]}: $
                            {rowTotal.toFixed(2)}
                        </p>
                    </div>
                </div>
            )}

            <div className="mt-3 flex items-center justify-between text-xs text-gray-500 flex-wrap gap-2">
                <span>
                    linha = dia · coluna = hora · clique numa célula pra fixar ·
                    total ${grandTotal.toLocaleString("pt-BR", { maximumFractionDigits: 2 })}
                </span>
                <div className="flex items-center gap-3">
                    <span className="flex items-center gap-1">
                        <span className="w-3 h-3 rounded-sm bg-violet-200" />
                        baixo
                    </span>
                    <span className="flex items-center gap-1">
                        <span className="w-3 h-3 rounded-sm bg-violet-500" />
                        médio
                    </span>
                    <span className="flex items-center gap-1">
                        <span className="w-3 h-3 rounded-sm bg-amber-500" />
                        pico
                    </span>
                    {selected && (
                        <button
                            type="button"
                            onClick={() => setSelected(null)}
                            className="text-violet-700 font-medium hover:underline"
                        >
                            limpar seleção
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

function EmptyMsg() {
    return (
        <div className="text-center py-8">
            <Layers className="w-6 h-6 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-400">Sem dados no período</p>
        </div>
    );
}
